<template>
  <div
    class="bg-music-row -hull"
    :class="{
      '-selected': isSelected,
      '-playing': isPlaying,
      '-animate-removal': processingDelete,
    }"
    @click="onClickRow"
  >
    <div v-if="showDevtool" class="-dev-tool">
      <div>
        <span
          title="assetUUID"
          v-text="audio.metadata.assetUUID"
          style="background-color: rgba(234, 255, 21, 0.16)"
        />
        <span
          title="duration"
          v-text="audio.metadata.duration"
          style="background-color: rgba(0, 3, 255, 0.16)"
        />
        <span
          title="fileSize"
          style="background-color: rgba(197, 197, 197, 0.16)"
          >{{ audio.metadata.fileSize | fileSizeString }}</span
        >
        <span
          title="ownerUserDbId"
          v-text="audio.metadata.ownerUserDbId"
          style="background-color: rgba(0, 249, 0, 0.16)"
        />
        <span
          title="restrictions"
          v-text="audio.metadata.restrictions"
          style="background-color: rgba(0, 249, 0, 0.16)"
        />
      </div>
    </div>
    <div class="-hull-head">
      <template v-if="!isUploading">
        <i
          v-if="isPlaying"
          class="-pause wticon wticon-pause2"
          :title="'bgMusic.tip.pause' | culture"
          @click.stop="onTogglePlaying"
        />
        <i
          v-else
          class="-play wticon wticon-play_4 -visible-onhover"
          :title="'bgMusic.tip.play' | culture"
          @click.stop="onTogglePlaying"
        />
      </template>
      <template v-if="!isUploading">
        <i
          v-if="audio.favorite"
          class="-favorite wticon wticon-star_full -it-is"
          :title="'bgMusic.tip.unFavorite' | culture"
          @click.stop="onFavorite"
        />
        <i
          v-else
          class="-favorite wticon wticon-star_line -visible-onhover"
          :title="'bgMusic.tip.favorite' | culture"
          @click.stop="onFavorite"
        />
      </template>
    </div>
    <div class="-cell -hull-c1">
      <span
        :class="{ '-sl-ellipsis': isTitleTooLong }"
        :title="titleTooltip"
        v-text="title"
      />
    </div>
    <div class="-cell -hull-c2" v-text="date" :title="isoDate" />
    <div class="-cell -hull-c3" v-text="genre" />
    <div class="-cell -hull-c4" v-text="mood" />
    <div class="-cell -hull-c5" v-text="pace" />
    <div class="-hull-tail">
      <div v-show="isSelected" class="-in-use">
        <i class="wticon wticon-toggle_v" />
        <span>{{ 'bgMusic.infoSelected' | culture }}</span>
      </div>

      <wt-button
        v-show="isSelected"
        type="error"
        :title="'bgMusic.tip.unselect' | culture"
        :text="'bgMusic.btnUnselect' | culture"
        @click.stop="onSelect"
        class="-btn-unselect -show-onhover"
      />

      <div
        v-if="showUploadingError"
        :title="'bgMusic.tip.uploadFailure' | culture"
        class="-upload-failure"
      >
        <i class="wticon wticon-warning-s" />
        <span>{{ 'bgMusic.upload.failure' | culture }}</span>
      </div>

      <wt-button
        v-if="showDelete"
        icon="wticon-trash2"
        type="transparent"
        :disabled="processingDelete"
        :processing="processingDelete"
        :title="'bgMusic.tip.delete' | culture"
        @click.stop="onDelete"
        class="-show-onhover"
      />

      <wt-button
        v-show="showSelect"
        type="secondary"
        :title="'bgMusic.tip.select' | culture"
        :text="'bgMusic.btnSelect' | culture"
        @click.stop="onSelect"
        class="-btn-select -show-onhover"
      />

      <wt-progress-native
        v-if="showUploadingProgress"
        :value="progressValue"
        :max="100"
        :label-left="progressLabel"
        class="-uploading"
      />
    </div>
  </div>
</template>

<script>
import app from 'app';
import api from '../api';
import { mapActions } from 'vuex';
import moment from 'moment';
import ExternalScripts from '#/externalScripts';

export default {
  data() {
    return {
      showDevtool: false,
      processingDelete: false,
    };
  },

  props: {
    onFinalClick: { type: Function, required: false, default: _.noop },
    audio: { type: Object, required: true },
  },

  computed: {
    showSelect() {
      return !this.isSelected && !this.isUploading;
    },
    showDelete() {
      return (
        this.audio.metadata.privacy !== 'PUBLIC' &&
        !this.isSelected &&
        (!this.isUploading || this.showUploadingError)
      );
    },
    showUploadingProgress() {
      return this.isUploading && !this.showUploadingError;
    },
    progressValue() {
      if (this.audio.progress && 'percentage' in this.audio.progress) {
        return this.audio.progress.percentage;
      }

      return false;
    },
    progressLabel() {
      if (this.audio.metadata.uploadStatus !== app.uploadStatus.UPLOAD) {
        return app.culture.get('bgMusic.upload.processing');
      } else {
        if (!this.progressValue) {
          return app.culture.get('bgMusic.upload.inQueue');
        } else {
          return app.culture.get('bgMusic.upload.progress');
        }
      }
    },
    showUploadingError() {
      return this.audio.metadata.uploadStatus === app.uploadStatus.FAILED;
    },

    isSelected() {
      return this.audioId === this.$store.getters.selectedId;
    },
    isPlaying() {
      return this.audioId === this.$store.getters.playingId;
    },
    isUploading() {
      return this.audio.metadata.uploadStatus !== app.uploadStatus.DONE;
    },
    pace() {
      return this.$store.state.PACE_MAP[this.audio.metadata.pace] || '';
    },
    mood() {
      return this.audio.metadata.mood;
    },
    genre() {
      return this.audio.metadata.genre;
    },
    isoDate() {
      return moment(this.audio.metadata.creationDate).format(
        'YYYY-MM-DD HH:mm'
      );
    },
    date() {
      return moment(this.audio.metadata.creationDate).format('L');
    },
    title() {
      return this.audio.metadata.title;
    },
    isTitleTooLong() {
      return app.namespace.Util.measureText.isLonger(
        this.audio.metadata.title,
        { size: 16, name: ExternalScripts.MAIN_FONT },
        this.$store.state.MAX_TITLE_WIDTH
      );
    },
    titleTooltip() {
      return this.isTitleTooLong ? this.audio.metadata.title : false;
    },
    audioId() {
      return api.getId(this.audio);
    },
  },

  methods: {
    ...mapActions([
      'TOGGLE_SELECT',
      'TOGGLE_FAVORITE',
      'TOGGLE_PLAYING',
      'DELETE',
    ]),

    onTogglePlaying() {
      this.TOGGLE_PLAYING(this.audio);
    },

    onSelect() {
      this.TOGGLE_SELECT(this.audio).then(() => {
        this.onFinalClick();
      });
    },

    onDelete() {
      const pDelete = new _.ColdPromise();

      if (app.stash.get('skip_confirm_bgmusic_delete')) {
        pDelete.resolve();
      } else {
        app.dialog
          .confirm({
            type: app.dialog.types.CONFIRM_2,
            showCheckbox: true,
            ...app.culture.bgMusic.confirmDelete,
          })
          .then((data) => {
            if (data.metadata && data.metadata.checkboxValue) {
              app.stash.set('skip_confirm_bgmusic_delete', true);
            }
            if (data.action) {
              pDelete.resolve();
            }
          });
      }

      pDelete.then(() => {
        this.processingDelete = true;
        this.DELETE(this.audio).finally(() => {
          this.processingDelete = false;
          this.onFinalClick();
        });
      });
    },

    onFavorite() {
      this.TOGGLE_FAVORITE({
        audio: this.audio,
        favorite: !this.audio.favorite,
      });
    },

    onClickRow() {
      this.$log(this.audio);
    },
  },
};
</script>
